// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Camera from "@insite/mobius/Icons/Camera";
import FileText from "@insite/mobius/Icons/FileText";
import Rss from "@insite/mobius/Icons/Rss";
import User from "@insite/mobius/Icons/User";
import Video from "@insite/mobius/Icons/Video";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AppStore from "@steiner/XDL/Icons/AppStore";
import Facebook from "@steiner/XDL/Icons/Facebook";
import Instagram from "@steiner/XDL/Icons/Instagram";
import Linkedin from "@steiner/XDL/Icons/LinkedIn";
import PlayStore from "@steiner/XDL/Icons/PlayStore";
import Twitter from "@steiner/XDL/Icons/Twitter";
import Youtube from "@steiner/XDL/Icons/Youtube";
import * as React from "react";
import { FC } from "react";

const enum fields {
    sectionTitle = "sectionTitle",
    links = "links",
}

const enum icons {
    instagram = "Instagram",
    facebook = "Facebook",
    youtube = "Youtube",
    twitter = "Twitter",
    xTwitter = "X",
    rss = "Rss",
    user = "User",
    linkedin = "Linkedin",
    camera = "Camera",
    video = "Video",
    notebook = "Notebook",
    appStore = "AppStore",
    playStore = "PlayStore",
}

const enum visibilityState {
    both = "both",
    label = "label",
    icon = "icon",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.sectionTitle]: string;
        [fields.links]: LinkModel[];
    };
}

interface LinkModel {
    fields: {
        title: string;
        icon: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overriddenTitle: string;
    };
}

export interface FooterSocialLinksStyles {
    container?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    sectionTitle?: TypographyProps;
}

export const footerSocialLinksStyles: FooterSocialLinksStyles = {};

const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.youtube:
            return Youtube;
        case icons.twitter:
            return Twitter;
        case icons.xTwitter:
            return Twitter;
        case icons.facebook:
            return Facebook;
        case icons.instagram:
            return Instagram;
        case icons.linkedin:
            return Linkedin;
        case icons.notebook:
            return FileText;
        case icons.rss:
            return Rss;
        case icons.user:
            return User;
        case icons.video:
            return Video;
        case icons.camera:
            return Camera;
        case icons.appStore:
            return AppStore;
        case icons.playStore:
            return PlayStore;
        default:
            break;
    }

    return "";
};

const styles = footerSocialLinksStyles;
const FooterSocialLinks: FC<OwnProps> = ({ fields }) => {
    const { links } = useGetLinks(fields.links, o => o.fields.destination);

    return (
        <StyledWrapper {...styles.container}>
            {fields.sectionTitle && <Typography {...styles.sectionTitle}>{fields.sectionTitle}</Typography>}
            <StyledWrapper className="social-links">
                {links.map((link, index) => {
                    return (
                        <StyledWrapper
                            className="link-wrapper"
                             
                            key={index}
                        >
                            {link.url && (
                                <Link
                                    className="social-link"
                                    {...styles.link}
                                    href={link.url}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                />
                            )}
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FooterSocialLinks,
    definition: {
        group: "Footer",
        icon: "thumbs-up",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.sectionTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "icon",
                        displayName: "Select Icon",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Instagram", value: icons.instagram },
                            { displayName: "Youtube", value: icons.youtube },
                            { displayName: "Facebook", value: icons.facebook },
                            { displayName: "Twitter", value: icons.twitter },
                            { displayName: "X", value: icons.twitter },
                            { displayName: "Rss", value: icons.rss },
                            { displayName: "User", value: icons.user },
                            { displayName: "Linkedin", value: icons.linkedin },
                            { displayName: "Camera", value: icons.camera },
                            { displayName: "Video", value: icons.video },
                            { displayName: "Notebook", value: icons.notebook },
                            { displayName: "App Store", value: icons.appStore },
                            { displayName: "Play Store", value: icons.playStore },
                        ],
                        defaultValue: "",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Url",
                        },
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
